import { useState, useCallback, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import { History as HistoryIcon, CalendarDays, LogOut, Shield, Stethoscope, Settings as SettingsIcon } from "lucide-react";
import Dashboard from "@/pages/Dashboard";
import History from "@/pages/History";
import CalendarView from "@/pages/CalendarView";
import AdminPanel from "@/pages/AdminPanel";
import Settings from "@/pages/Settings";
import Auth from "@/pages/Auth";
import LandingPage from "@/LandingPage";
import FreeWRVUCalculator from "@/pages/FreeWRVUCalculator";
import RVUArticles from "@/pages/RVUArticles";
import HowToTrackRVUs from "@/pages/articles/HowToTrackRVUs";
import MissedBillingCodes from "@/pages/articles/MissedBillingCodes";
import RVUsAndPaycheck from "@/pages/articles/RVUsAndPaycheck";
import CompleteRegistration from "@/pages/CompleteRegistration";
import Subscription from "@/pages/Subscription";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import OnboardingTutorial from "@/components/OnboardingTutorial";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/contexts/ThemeContext";
import { fetchTaxRate, fetchWrvuRate, fetchAdminStats, fetchSubscriptionStatus } from "@/lib/api";

// Protected Route wrapper - redirects to /login if not authenticated
function ProtectedRoute({ children, requireSubscription = true }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    if (isAuthenticated && requireSubscription) {
      fetchSubscriptionStatus()
        .then(status => {
          setSubscriptionStatus(status);
          setCheckingSubscription(false);
        })
        .catch(() => {
          setCheckingSubscription(false);
        });
    } else {
      setCheckingSubscription(false);
    }
  }, [isAuthenticated, requireSubscription]);

  if (loading || checkingSubscription) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-blue-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login, but save the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check subscription if required
  if (requireSubscription && subscriptionStatus && !subscriptionStatus.has_access) {
    return <Navigate to="/subscription" replace />;
  }

  return children;
}

// Subscription Route wrapper - for subscription page (authenticated but no subscription check)
function SubscriptionRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-blue-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// Auth Route wrapper - redirects authenticated users appropriately
function AuthRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscriptionStatus()
        .then(status => {
          setSubscriptionStatus(status);
          setCheckingSubscription(false);
        })
        .catch(() => {
          setCheckingSubscription(false);
        });
    } else {
      setCheckingSubscription(false);
    }
  }, [isAuthenticated]);

  if (loading || (isAuthenticated && checkingSubscription)) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-blue-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Check if user has subscription access
    if (subscriptionStatus?.has_access) {
      // User has access, redirect to app
      const from = location.state?.from?.pathname || "/rvu-tracker";
      return <Navigate to={from} replace />;
    } else {
      // User doesn't have access, redirect to subscription page
      return <Navigate to="/subscription" replace />;
    }
  }

  return children;
}

// Main authenticated app with navigation
function MainApp() {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDate, setEditDate] = useState(null);
  const [taxRate, setTaxRate] = useState(24.0);
  const [wrvuRate, setWrvuRate] = useState(25.0);
  const [adminNotifications, setAdminNotifications] = useState(0);
  const [trackerDate, setTrackerDate] = useState(new Date());
  const [showTutorial, setShowTutorial] = useState(false);

  // Determine active tab from URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.startsWith("/rvu-tracker/calendar")) return "calendar";
    if (path.startsWith("/rvu-tracker/history")) return "history";
    if (path.startsWith("/rvu-tracker/settings")) return "settings";
    if (path.startsWith("/rvu-tracker/admin")) return "admin";
    return "tracker";
  };
  
  const activeTab = getActiveTab();

  // Tutorial version check
  const TUTORIAL_VERSION = "v2";
  
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const tutorialKey = `tutorial_seen_${user.id}_${TUTORIAL_VERSION}`;
    const hasSeen = localStorage.getItem(tutorialKey);
    if (!hasSeen) {
      setTimeout(() => setShowTutorial(true), 500);
    }
  }, [isAuthenticated, user?.id]);

  const handleTutorialComplete = (dontShowAgain = false) => {
    if (user?.id && dontShowAgain) {
      localStorage.setItem(`tutorial_seen_${user.id}_${TUTORIAL_VERSION}`, "true");
    }
    setShowTutorial(false);
  };

  const openTutorial = () => {
    setShowTutorial(true);
  };

  // Load tax rate and wRVU rate
  useEffect(() => {
    if (!isAuthenticated) return;
    const year = new Date().getFullYear();
    fetchTaxRate(year).then(data => setTaxRate(data.rate)).catch(() => {});
    fetchWrvuRate(year).then(data => setWrvuRate(data.rate)).catch(() => {});
  }, [isAuthenticated]);

  // Fetch admin notifications
  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    const fetchNotifications = async () => {
      try {
        const stats = await fetchAdminStats();
        setAdminNotifications((stats.pending_users || 0) + (stats.unread_messages || 0));
      } catch {
        // Silent fail
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isAdmin]);

  const handleSaved = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleEditShift = useCallback((dateStr) => {
    setEditDate(dateStr);
    navigate("/rvu-tracker");
  }, [navigate]);

  const handleEditDateConsumed = useCallback(() => {
    setEditDate(null);
  }, []);

  const handleTaxRateChanged = useCallback((newRate) => {
    setTaxRate(newRate);
  }, []);

  const handleWrvuRateChanged = useCallback((newRate) => {
    setWrvuRate(newRate);
  }, []);

  const retentionRate = (100 - taxRate) / 100;

  const navigateToTab = (tab) => {
    const routes = {
      tracker: "/rvu-tracker",
      calendar: "/rvu-tracker/calendar",
      history: "/rvu-tracker/history",
      settings: "/rvu-tracker/settings",
      admin: "/rvu-tracker/admin"
    };
    navigate(routes[tab] || "/rvu-tracker");
  };

  return (
    <div className={`min-h-screen flex flex-col ${theme === 'dark' ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
      <Toaster
        theme={theme}
        position="top-right"
        closeButton={true}
        toastOptions={{
          duration: 2500,
          style: theme === 'dark' ? {
            background: '#1e293b',
            border: '1px solid #334155',
            color: '#f8fafc',
          } : {
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            color: '#111827',
          },
        }}
      />

      <nav className={`sticky top-0 z-40 border-b backdrop-blur-lg ${
        theme === 'dark' 
          ? 'border-slate-700/40 bg-[#0f172a]/90' 
          : 'border-gray-200 bg-white/90'
      }`}>
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/rvu-tracker")}>
              <img src="/logo.png" alt="Doc RVU Tracker" className="w-6 h-6 rounded-md" />
              <span className={`text-base font-bold tracking-wide ${
                theme === 'dark' ? 'text-slate-200' : 'text-gray-900'
              }`} style={{ fontFamily: "'Manrope', sans-serif" }}>
                Doc RVU Tracker
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-1 rounded-lg p-1 ${
                theme === 'dark' ? 'bg-slate-800/60' : 'bg-gray-100'
              }`}>
                <button
                  data-testid="nav-tracker"
                  onClick={() => navigateToTab("tracker")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    activeTab === "tracker" 
                      ? (theme === 'dark' ? "bg-slate-700/80 text-slate-100" : "bg-white text-gray-900 shadow-sm")
                      : (theme === 'dark' ? "text-slate-500 hover:text-slate-300" : "text-gray-600 hover:text-gray-900")
                  }`}
                >
                  <Stethoscope size={13} />
                  Tracker
                </button>
                <button
                  data-testid="nav-calendar"
                  onClick={() => navigateToTab("calendar")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    activeTab === "calendar" 
                      ? (theme === 'dark' ? "bg-slate-700/80 text-slate-100" : "bg-white text-gray-900 shadow-sm")
                      : (theme === 'dark' ? "text-slate-500 hover:text-slate-300" : "text-gray-600 hover:text-gray-900")
                  }`}
                >
                  <CalendarDays size={13} />
                  Calendar
                </button>
                <button
                  data-testid="nav-history"
                  onClick={() => navigateToTab("history")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    activeTab === "history" 
                      ? (theme === 'dark' ? "bg-slate-700/80 text-slate-100" : "bg-white text-gray-900 shadow-sm")
                      : (theme === 'dark' ? "text-slate-500 hover:text-slate-300" : "text-gray-600 hover:text-gray-900")
                  }`}
                >
                  <HistoryIcon size={13} />
                  History
                </button>
                <button
                  data-testid="nav-settings"
                  onClick={() => navigateToTab("settings")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    activeTab === "settings" 
                      ? (theme === 'dark' ? "bg-slate-700/80 text-slate-100" : "bg-white text-gray-900 shadow-sm")
                      : (theme === 'dark' ? "text-slate-500 hover:text-slate-300" : "text-gray-600 hover:text-gray-900")
                  }`}
                >
                  <SettingsIcon size={13} />
                  Settings
                </button>
                {isAdmin && (
                  <button
                    data-testid="nav-admin"
                    onClick={() => navigateToTab("admin")}
                    className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                      activeTab === "admin" ? "bg-amber-500/20 text-amber-300" : "text-amber-500/70 hover:text-amber-400"
                    }`}
                  >
                    <Shield size={13} />
                    Admin
                    {adminNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-500 text-white rounded-full">
                        {adminNotifications > 99 ? "99+" : adminNotifications}
                      </span>
                    )}
                  </button>
                )}
              </div>

              {/* User menu */}
              <div className="flex items-center gap-2 pl-2 border-l border-slate-700/50">
                <span className="text-xs text-slate-500 hidden sm:block">{user?.email}</span>
                <button
                  data-testid="logout-btn"
                  onClick={logout}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  title="Sign out"
                >
                  <LogOut size={14} />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8 flex-grow">
        <Routes>
          <Route path="/" element={
            <Dashboard
              onSaved={handleSaved}
              editDate={editDate}
              onEditDateConsumed={handleEditDateConsumed}
              retentionRate={retentionRate}
              taxRate={taxRate}
              wrvuRate={wrvuRate}
              trackerDate={trackerDate}
              onTrackerDateChange={setTrackerDate}
            />
          } />
          <Route path="/calendar" element={
            <CalendarView
              key={refreshKey}
              onEditShift={handleEditShift}
              taxRate={taxRate}
              retentionRate={retentionRate}
              onTaxRateChanged={handleTaxRateChanged}
              wrvuRate={wrvuRate}
              onWrvuRateChanged={handleWrvuRateChanged}
            />
          } />
          <Route path="/history" element={
            <History refreshKey={refreshKey} retentionRate={retentionRate} taxRate={taxRate} wrvuRate={wrvuRate} />
          } />
          <Route path="/settings" element={
            <Settings
              taxRate={taxRate}
              wrvuRate={wrvuRate}
              onTaxRateChanged={handleTaxRateChanged}
              onWrvuRateChanged={handleWrvuRateChanged}
              onOpenTutorial={openTutorial}
            />
          } />
          {isAdmin && (
            <Route path="/admin" element={<AdminPanel />} />
          )}
          {/* Catch-all redirect to tracker */}
          <Route path="*" element={<Navigate to="/rvu-tracker" replace />} />
        </Routes>
      </main>
      
      {/* Onboarding Tutorial */}
      <OnboardingTutorial
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
        onComplete={handleTutorialComplete}
      />
      
      {/* Footer with Copyright */}
      <footer className={`border-t backdrop-blur-lg mt-8 py-6 ${
        theme === 'dark' 
          ? 'border-slate-700/40 bg-[#0f172a]/95' 
          : 'border-gray-200 bg-white/95'
      }`}>
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className={`flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 text-sm ${
            theme === 'dark' ? 'text-slate-400' : 'text-gray-600'
          }`}>
            <div className="text-center sm:text-left">
              <p>© 2026 Doc RVU Tracker. All Rights Reserved.</p>
            </div>
            <div className="text-center sm:text-right">
              <p className={theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}>
                Built for medical professionals
              </p>
              <p className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>
                DocRVUTracker@gmail.com
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Root path - Landing page */}
          <Route path="/" element={<LandingPage />} />

          {/* Free wRVU Calculator - public tool */}
          <Route path="/free-rvu-calculator" element={<FreeWRVUCalculator />} />

          {/* Articles / blog - public pages */}
          <Route path="/rvu-articles" element={<RVUArticles />} />
          <Route path="/rvu-articles/how-to-track-rvus" element={<HowToTrackRVUs />} />
          <Route path="/rvu-articles/missed-billing-codes" element={<MissedBillingCodes />} />
          <Route path="/rvu-articles/rvus-and-paycheck" element={<RVUsAndPaycheck />} />

          {/* Post-payment account completion */}
          <Route path="/complete-registration" element={<CompleteRegistration />} />

          {/* Auth routes */}
          <Route path="/login" element={
            <AuthRoute>
              <Auth mode="login" />
            </AuthRoute>
          } />
          <Route path="/register" element={
            <AuthRoute>
              <Auth mode="register" />
            </AuthRoute>
          } />
          
          {/* Subscription routes (authenticated but no subscription check) */}
          <Route path="/subscription" element={
            <SubscriptionRoute>
              <Subscription />
            </SubscriptionRoute>
          } />
          <Route path="/subscription/success" element={
            <SubscriptionRoute>
              <SubscriptionSuccess />
            </SubscriptionRoute>
          } />
          
          {/* Protected app routes (require subscription) */}
          <Route path="/rvu-tracker/*" element={
            <ProtectedRoute requireSubscription={true}>
              <MainApp />
            </ProtectedRoute>
          } />
          
          {/* No catch-all redirect - landing page is external at docrvutracker.com */}
          {/* Unknown routes will show nothing, which is expected */}
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
